import { Badge, Button, cn } from '@design-system';
import { PageLink, HrefLink } from '@/components';
import { OnboardingDownloadReport } from '@/pages';

import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { useSlideConfig, useOnboardingNavigation } from '@/features/client-onboarding';

import { selectClient } from '@/model';
import { toast as sonnerToast } from 'sonner';

export function OnboardingFooter() {
  const { slidePage } = useSlideConfig();

  if (!slidePage) return null;

  return <OnboardingFooterContent />;
}

function OnboardingFooterContent() {
  const client = useSelector(selectClient)!;
  const {
    slidePage,
    nextButtonLabel,
    nextSlidePage,
    previousSlidePage,
    optionalCtaLabel,
    optionalCtaPage,
    secondaryNextButtonLabel,
    secondaryNextSlidePage,
    disableNextButton,
    errorModalMessage,
    shouldHideNextNavigation,
    onNextClick,
    onBackClick,
  } = useSlideConfig();

  const navigation = useOnboardingNavigation({ slidePage: slidePage! });

  const { isFirstSlide, fromLtcPlanning, syncSupportProviders } = navigation;

  const nextSlide = navigation.getNextSlide(nextSlidePage);
  const prevSlide = navigation.getPrevSlide(previousSlidePage);

  const { clientId, clientEmail, clientFirstName, advisorId, clientLastName, surveys } = client;

  const [toastId, setToastId] = useState<string | number | null>(null);

  useEffect(() => {
    if (!disableNextButton && toastId) {
      sonnerToast.dismiss(toastId);
      setToastId(null);
    }
  }, [disableNextButton, toastId]);

  const showErrorToast = (message?: string) => {
    const id = sonnerToast.warning('Action Required', {
      description: message || errorModalMessage || 'Please select an answer to continue',
    });
    setToastId(id);
  };

  const handleNextButtonClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (disableNextButton) {
      event.preventDefault();
      showErrorToast();
      return;
    }

    if (onNextClick) {
      event.preventDefault();
      onNextClick();
    }
    syncSupportProviders();
  };

  const feedbackSurveyUrl = useMemo(() => {
    const surveyId = surveys?.length > 0 ? surveys[surveys.length - 1].surveyId : '';

    return `https://waterlily.typeform.com/to/m4H0GpSY#consumer_email=${clientEmail}&consumer_first_name=${clientFirstName}&advisor_id=${advisorId}&survey_id=${surveyId}&organization_name=xxxxx&consumer_last_name=${clientLastName}`;
  }, [clientEmail, clientFirstName, advisorId, clientLastName, surveys]);

  const isDownloadReport = slidePage === OnboardingDownloadReport;

  const secondaryNextButton =
    secondaryNextSlidePage && secondaryNextButtonLabel ? (
      <PageLink
        className="w-full md:w-auto"
        to={secondaryNextSlidePage}
        targetProps={{ clientId }}
      >
        <Button
          variant="outline"
          size="lg"
          className="w-full rounded-full md:w-auto"
        >
          {secondaryNextButtonLabel}
        </Button>
      </PageLink>
    ) : null;

  const nextButton = useMemo(() => {
    if (shouldHideNextNavigation) return null;

    if (isDownloadReport) {
      return (
        <HrefLink
          to={feedbackSurveyUrl}
          rel="noopener"
          target="_blank"
        >
          <Button
            size="lg"
            className="rounded-full"
            disabled={disableNextButton}
          >
            {nextButtonLabel}
          </Button>
        </HrefLink>
      );
    }

    return (
      <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-2">
        {secondaryNextButton}
        <PageLink
          onClick={handleNextButtonClick}
          to={nextSlide}
          targetProps={{ clientId }}
          data-testid="onboarding-next-link"
        >
          <Button
            size="lg"
            className={cn('rounded-full', disableNextButton && 'cursor-not-allowed')}
            data-intercom-target="Next Button"
            data-testid="onboarding-next-button"
            disabled={disableNextButton}
          >
            {nextButtonLabel}
            <ChevronRight />
          </Button>
        </PageLink>
      </div>
    );
  }, [
    shouldHideNextNavigation,
    isDownloadReport,
    feedbackSurveyUrl,
    nextButtonLabel,
    disableNextButton,
    secondaryNextButton,
    nextSlide,
    clientId,
  ]);

  const backButton = useMemo(() => {
    if (isFirstSlide) return null;

    if (onBackClick) {
      return (
        <Button
          variant="outline"
          size="lg"
          className="rounded-full"
          onClick={onBackClick}
          data-testid="onboarding-back-button"
        >
          <ChevronLeft />
          Back
        </Button>
      );
    }

    if (fromLtcPlanning) {
      return (
        <Button
          variant="outline"
          size="lg"
          className="rounded-full"
          onClick={() => window.history.back()}
        >
          <ChevronLeft />
          Back
        </Button>
      );
    }

    if (!prevSlide) return null;

    return (
      <PageLink
        onClick={syncSupportProviders}
        to={prevSlide}
        targetProps={{ clientId }}
        data-testid="onboarding-back-link"
      >
        <Button
          variant="outline"
          size="lg"
          className="rounded-full"
          data-testid="onboarding-back-button"
        >
          <ChevronLeft />
          Back
        </Button>
      </PageLink>
    );
  }, [isFirstSlide, onBackClick, fromLtcPlanning, prevSlide, clientId, syncSupportProviders]);

  const optionalCtaButton =
    optionalCtaLabel && optionalCtaPage ? (
      <PageLink
        to={optionalCtaPage}
        targetProps={{ clientId }}
      >
        <Button
          variant="outline"
          size="lg"
          className="relative rounded-full"
          data-testid="onboarding-optional-button"
        >
          <Badge
            variant="secondary"
            className="border-background absolute right-0 top-0 -translate-y-1/2 border-2"
          >
            Optional
          </Badge>
          {optionalCtaLabel}
        </Button>
      </PageLink>
    ) : null;

  return (
    <div className="border-border bg-background sticky bottom-0 z-20 border-t p-4 pr-20 md:p-6 md:pr-20 xl:pr-6">
      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-4">
        <div>{backButton}</div>
        <div className="flex flex-wrap items-center gap-3 md:ml-auto">
          {optionalCtaButton}
          {nextButton}
        </div>
      </div>
    </div>
  );
}
